/**
 * MfaEmailVerifyFlow — email-code 6-digit challenge (Email-MFA B3).
 *
 * A variant of ``MfaVerifyFlow`` for the email-code MFA method: the user
 * receives a 6-digit code by email (not from an authenticator app) and
 * there are NO recovery codes. So this surface is the same OTP-entry layout
 * (reuses ``OtpCodeInput`` + ``FormField`` + ``Button``, autosubmit on 6
 * digits) MINUS the recovery toggle, PLUS a "Resend code" affordance with a
 * 45s cooldown countdown.
 *
 * Reuse, not divergence:
 *   - Verify success → ``loginSuccess(access_token)`` + ``onComplete()`` —
 *     IDENTICAL to the TOTP path (same session/redirect handling).
 *   - 401 ``invalid_token`` (pending token expired) → full-card expired swap,
 *     ``onExpired()`` — same as TOTP.
 *   - 401 ``invalid_mfa_code`` → a single GENERIC error
 *     ("That code is incorrect or expired"); wrong / expired / consumed /
 *     none are NOT distinguished (matches the backend's anti-oracle 401).
 *   - 429 → lockout banner (verify) / cooldown countdown (resend), driven by
 *     ``retry_after_seconds``.
 *
 * Security: the code lives ONLY in the input field state — never persisted,
 * logged, or echoed. ``code_email_sent:false`` from the login response shows
 * a calm "couldn't email — resend" banner so the user is never stranded.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { UseMutationResult } from "@tanstack/react-query";
import { Button } from "@/components/common/Button";
import { FormField } from "@/components/common/FormField";
import { OtpCodeInput } from "@/components/auth/OtpCodeInput";
import { ApiError } from "@/lib/apiError";
import { useAuthStore } from "@/stores/useAuthStore";
import type {
  MfaEmailResendResponse,
  MfaVerifyResponse,
} from "@/hooks/queries/auth/useMfaVerify";

/** Per-user resend cooldown (seconds). Mirrors the backend's 45s window. */
const RESEND_COOLDOWN_SECONDS = 45;

type VerifyMutation = UseMutationResult<
  MfaVerifyResponse,
  ApiError,
  { code: string }
>;
type ResendMutation = UseMutationResult<
  MfaEmailResendResponse,
  ApiError,
  void
>;

interface MfaEmailVerifyFlowProps {
  verifyMutation: VerifyMutation;
  resendMutation: ResendMutation;
  onComplete: () => void;
  onExpired: () => void;
  /**
   * From the login response's ``code_email_sent``. ``false`` ⇒ the code
   * email failed to send — show the couldn't-email/resend affordance up
   * front instead of a bare challenge.
   */
  codeEmailSent: boolean | null;
}

type Banner =
  | { kind: "none" }
  | { kind: "lockout"; minutes: number }
  | { kind: "resent" }
  // D16: a 429 on resend means NO new code was sent (rate-limited). Showing
  // the "resent" success banner here is a lie — distinct cooldown banner.
  | { kind: "cooldown"; seconds: number }
  | { kind: "sendFailed" }
  | { kind: "error"; text: string };

export function MfaEmailVerifyFlow({
  verifyMutation,
  resendMutation,
  onComplete,
  onExpired,
  codeEmailSent,
}: MfaEmailVerifyFlowProps): JSX.Element {
  const [code, setCode] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  // Seed the banner from the login send-result: a failed send opens on the
  // couldn't-email state so the user immediately sees the resend path.
  const [banner, setBanner] = useState<Banner>(
    codeEmailSent === false ? { kind: "sendFailed" } : { kind: "none" },
  );
  const [expired, setExpired] = useState(false);
  // Seconds remaining on the resend cooldown; 0 = resend enabled.
  const [cooldown, setCooldown] = useState(0);

  // Cooldown tick — decrement once a second until it hits zero. The interval
  // is cleared on unmount and whenever the cooldown is (re)armed.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => {
      setCooldown((s) => {
        const next = s <= 1 ? 0 : s - 1;
        // D16: when the cooldown elapses, drop the stale "please wait" banner
        // so the next resend isn't preceded by an outdated countdown message.
        if (next === 0) {
          setBanner((b) => (b.kind === "cooldown" ? { kind: "none" } : b));
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const lockedOut = banner.kind === "lockout";

  const submit = useCallback(
    (value: string): void => {
      setFieldError(null);
      if (
        banner.kind === "error" ||
        banner.kind === "resent" ||
        banner.kind === "cooldown"
      ) {
        setBanner({ kind: "none" });
      }
      verifyMutation.mutate(
        { code: value },
        {
          onSuccess: (data) => {
            useAuthStore.getState().loginSuccess(data.access_token);
            onComplete();
          },
          onError: (err) => {
            if (err.status === 401 && err.error === "invalid_token") {
              setExpired(true);
            } else if (err.status === 401) {
              // Generic — wrong / expired / consumed / none all look the same.
              setFieldError("That code is incorrect or expired.");
              setCode("");
            } else if (err.status === 429) {
              const detail = err.details as { retry_after_seconds?: number };
              const minutes = detail.retry_after_seconds
                ? Math.ceil(detail.retry_after_seconds / 60)
                : 15;
              setBanner({ kind: "lockout", minutes });
            } else {
              setBanner({
                kind: "error",
                text: "Our auth service hiccupped. Try again in a minute.",
              });
            }
          },
        },
      );
    },
    [banner.kind, verifyMutation, onComplete],
  );

  const handleSubmit = (): void => {
    if (code.length !== 6 || lockedOut) return;
    submit(code);
  };

  // Keep the autosubmit callback identity stable so OtpCodeInput's "fire
  // once per transition-to-6-digits" guard doesn't re-fire on a re-render.
  // Latest ``submit``/``code`` are read through refs.
  const submitRef = useRef(submit);
  const codeRef = useRef(code);
  submitRef.current = submit;
  codeRef.current = code;
  const handleAutoSubmit = useCallback((): void => {
    if (lockedOut) return;
    submitRef.current(codeRef.current);
  }, [lockedOut]);

  const handleResend = (): void => {
    if (cooldown > 0 || resendMutation.isPending) return;
    setFieldError(null);
    resendMutation.mutate(undefined, {
      onSuccess: () => {
        setBanner({ kind: "resent" });
        setCooldown(RESEND_COOLDOWN_SECONDS);
      },
      onError: (err) => {
        if (err.status === 429) {
          const detail = err.details as { retry_after_seconds?: number };
          const remaining = detail.retry_after_seconds ?? RESEND_COOLDOWN_SECONDS;
          // D16 FIX: 429 means rate-limited — NO new code was sent. Arm the
          // server-authoritative cooldown but show a "please wait" banner, NOT
          // the "a new code has been sent" success message (which was the bug).
          setCooldown(remaining);
          setBanner({ kind: "cooldown", seconds: remaining });
        } else if (err.status === 401 && err.error === "invalid_token") {
          setExpired(true);
        } else {
          setBanner({
            kind: "error",
            text: "We couldn't send a new code. Try again in a minute.",
          });
        }
      },
    });
  };

  if (expired) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-text-secondary dark:text-brand-100/70">
          Your sign-in attempt timed out. Sign in again.
        </p>
        <Button type="button" className="w-full" onClick={onExpired}>
          Back to sign in
        </Button>
      </div>
    );
  }

  const resendDisabled =
    cooldown > 0 || resendMutation.isPending || verifyMutation.isPending;

  return (
    <div className="flex flex-col gap-4">
      {banner.kind === "lockout" ? (
        <div
          role="alert"
          className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300"
        >
          Too many attempts. Try again in {banner.minutes}{" "}
          {banner.minutes === 1 ? "minute" : "minutes"}.
        </div>
      ) : null}
      {banner.kind === "sendFailed" ? (
        <div
          role="alert"
          className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300"
        >
          We couldn't email your code. Use Resend below to try again.
        </div>
      ) : null}
      {banner.kind === "resent" ? (
        <div
          role="status"
          className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300"
        >
          A new code has been sent. Check your email.
        </div>
      ) : null}
      {banner.kind === "cooldown" ? (
        <div
          role="status"
          className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300"
        >
          Please wait before requesting another code. You can resend in{" "}
          {banner.seconds} {banner.seconds === 1 ? "second" : "seconds"}.
        </div>
      ) : null}
      {banner.kind === "error" ? (
        <div
          role="alert"
          className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-400"
        >
          {banner.text}
        </div>
      ) : null}

      <FormField
        label="Verification code"
        htmlFor="mfa-email-code"
        error={fieldError ?? undefined}
      >
        <OtpCodeInput
          id="mfa-email-code"
          value={code}
          onChange={setCode}
          onAutoSubmit={handleAutoSubmit}
          hasError={fieldError !== null}
          disabled={verifyMutation.isPending || lockedOut}
          ariaLabel="Six-digit verification code from your email"
          autoFocus
        />
      </FormField>
      <Button
        type="button"
        className="w-full"
        loading={verifyMutation.isPending}
        disabled={code.length !== 6 || lockedOut}
        onClick={handleSubmit}
      >
        Verify and continue
      </Button>

      <div className="flex flex-col items-center gap-1 pt-1">
        <span className="text-xs text-text-secondary dark:text-brand-100/60">
          Didn't get a code?
        </span>
        <button
          type="button"
          className="text-sm text-accent-primary underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:text-text-secondary disabled:no-underline dark:text-accent-secondary dark:disabled:text-brand-100/50"
          onClick={handleResend}
          disabled={resendDisabled}
        >
          {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
        </button>
      </div>
    </div>
  );
}
